"use client";

import { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Users,
  UserPlus,
  Building2,
  Check,
  Loader2,
  CalendarDays,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import { Switch } from "@/app/_module/components/ui/switch";

import { useAgencyCalendar, useAssignAppointment } from "../hooks/queries";
import type { AgencyCalendarItem } from "../services/appointment-services";

/**
 * Agency Calendar — the owner's view of the whole team's week.
 *
 * Colour language (agreed with Moe):
 *   GREEN  — an agent owns it (assigned to an agent, or an agent booked it)
 *   RED    — booked by the owner/admin and NOT handed out yet
 *   SLATE  — the owner ran it themselves
 *
 * `ownerKind` is computed server-side because it depends on agency_users roles
 * the browser never sees. Assignment happens right here: click a day, tag an
 * agent, and it moves onto that agent's calendar.
 */

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Moe's spec: GREEN = agent appointment, RED = owner's own AND unassigned.
// Both red kinds share the colour; the day panel distinguishes them in words.
const KIND_DOT: Record<AgencyCalendarItem["ownerKind"], string> = {
  agent: "bg-emerald-500",
  unassigned: "bg-red-500",
  owner: "bg-red-500",
};
const KIND_CHIP: Record<AgencyCalendarItem["ownerKind"], string> = {
  agent: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  unassigned: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  owner: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

interface AgencyMember {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  /** Which agency they belong to — an owner assigning across a downline needs it
      to tell two "Mike"s apart. Already returned by /agencies/agents. */
  agencyName?: string;
  /** How many FURTHER agencies this person belongs to, beyond agencyName. */
  extraAgencies?: number;
}

/** Agency roster for the assign picker. Kept local so the tab is self-contained. */
function useAgencyMembers(enabled: boolean, includeHierarchy: boolean) {
  return useQuery<AgencyMember[]>({
    // includeHierarchy is part of the key: the two lists are different data, and
    // sharing one cache entry meant toggling "Include sub-agencies" left the
    // picker showing whichever list happened to be cached.
    queryKey: ["appointments", "agency-members", includeHierarchy],
    queryFn: async () => {
      // Follows the "Include sub-agencies" toggle. With it OFF the picker must
      // only offer your own agency — offering the whole downline there lets an
      // owner assign work to someone they are not currently looking at.
      const res = await api.get<unknown>({
        url: getApiUrl(
          `/agencies/agents${includeHierarchy ? "?includeHierarchy=true" : ""}`,
        ),
        auth: true,
      });
      const env = (res as { data?: unknown })?.data ?? res;
      const e = env as { data?: unknown; items?: unknown };
      const raw = (e?.items ?? e?.data ?? env) as unknown;
      const list = Array.isArray(raw) ? raw : ((raw as { items?: unknown[] })?.items ?? []);
      const rows = (list as Array<Record<string, unknown>>).map((m) => {
        const user = (m.user ?? m) as Record<string, unknown>;
        return {
          id: String(user.id ?? m.userId ?? m.id ?? ""),
          name: (user.name as string) || (m.name as string) || "",
          email: (user.email as string) || (m.email as string) || "",
          role: (m.role as string) || "",
          agencyName: (m.agencyName as string) || "",
        };
      }).filter((m) => m.id);

      // ⚠️ DEDUPE BY USER — this endpoint returns ONE ROW PER MEMBERSHIP, and
      // the owner cascade makes every upline owner a member of every descendant
      // agency. With includeHierarchy=true one person came back dozens of times,
      // which React reported as duplicate keys and which would have shown the
      // same name over and over in the picker.
      //
      // The first row wins for name/role: the endpoint sorts self first, then
      // alphabetically, so it is stable across refetches. Extra agencies are
      // summarised rather than listed — an owner sitting in 15 of them makes a
      // useless label, but "+14" still says they span a downline.
      const byUser = new Map<string, AgencyMember & { extraAgencies: number }>();
      for (const r of rows) {
        const seen = byUser.get(r.id);
        if (!seen) byUser.set(r.id, { ...r, extraAgencies: 0 });
        else if (r.agencyName && r.agencyName !== seen.agencyName) seen.extraAgencies += 1;
      }
      return [...byUser.values()];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function AgencyCalendar() {
  const [cursor, setCursor] = useState(() => new Date());
  const [includeSubAgencies, setIncludeSubAgencies] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);

  const { data, isLoading } = useAgencyCalendar({
    start: monthStart.toISOString(),
    end: monthEnd.toISOString(),
    includeSubAgencies,
  });
  const { data: members } = useAgencyMembers(true, includeSubAgencies);
  const assign = useAssignAppointment();
  // Which agent picker is filtered, and by what. Keyed by appointment so two
  // open pickers cannot share a query.
  const [agentQuery, setAgentQuery] = useState<Record<string, string>>({});

  const items = data?.items ?? [];

  // Bucket by local day — the grid is drawn in the viewer's timezone.
  const byDay = useMemo(() => {
    const map = new Map<string, AgencyCalendarItem[]>();
    for (const it of items) {
      const d = new Date(it.startTime);
      if (isNaN(d.getTime())) continue;
      const key = ymd(d);
      const arr = map.get(key) || [];
      arr.push(it);
      map.set(key, arr);
    }
    return map;
  }, [items]);

  const counts = useMemo(() => {
    let agent = 0, unassigned = 0, owner = 0;
    for (const it of items) {
      if (it.ownerKind === "agent") agent++;
      else if (it.ownerKind === "unassigned") unassigned++;
      else owner++;
    }
    return { agent, unassigned, owner };
  }, [items]);

  // Leading blanks so the 1st lands on the right weekday.
  const leadingBlanks = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();
  const cells: Array<Date | null> = [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1)),
  ];

  const todayKey = ymd(new Date());
  const selectedItems = selectedDay ? byDay.get(selectedDay) || [] : [];

  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-[170px] text-center text-lg font-semibold">{monthLabel}</div>
          <Button
            size="icon"
            variant="outline"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCursor(new Date())}>
            Today
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <Switch checked={includeSubAgencies} onCheckedChange={setIncludeSubAgencies} />
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" /> Include sub-agencies
            </span>
          </label>
          {/* Legend — the whole point of the view */}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Agent ({counts.agent})
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Owner / unassigned (
              {counts.unassigned + counts.owner})
            </span>
          </div>
        </div>
      </div>

      {/* Month grid */}
      <div className="overflow-hidden rounded-xl border">
        <div className="grid grid-cols-7 border-b bg-muted/40 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (!day) return <div key={`b${i}`} className="min-h-[104px] border-b border-r bg-muted/20" />;
            const key = ymd(day);
            const dayItems = byDay.get(key) || [];
            const isToday = key === todayKey;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedDay(dayItems.length ? key : null)}
                className={`min-h-[104px] border-b border-r p-1.5 text-left align-top transition-colors hover:bg-muted/50 ${
                  selectedDay === key ? "bg-muted/60" : ""
                }`}
              >
                <div className="mb-1 flex items-center justify-between">
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                      isToday ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                    }`}
                  >
                    {day.getDate()}
                  </span>
                  {dayItems.length > 2 && (
                    <span className="text-[10px] text-muted-foreground">{dayItems.length}</span>
                  )}
                </div>
                <div className="space-y-1">
                  {dayItems.slice(0, 2).map((it) => (
                    <div
                      key={it.id}
                      className={`truncate rounded border px-1.5 py-0.5 text-[10px] font-medium ${KIND_CHIP[it.ownerKind]}`}
                      title={`${it.title} — ${it.assignedToName || it.bookedByName || ""}`}
                    >
                      {new Date(it.startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}{" "}
                      {it.leadName || it.title}
                    </div>
                  ))}
                  {dayItems.length > 2 && (
                    <div className="pl-1 text-[10px] text-muted-foreground">
                      +{dayItems.length - 2} more
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading agency calendar…
        </div>
      )}

      {/* Day detail + assignment */}
      {selectedDay && selectedItems.length > 0 && (
        <div className="rounded-xl border p-4">
          <div className="mb-3 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">
              {new Date(`${selectedDay}T12:00:00`).toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </h3>
            <span className="text-xs text-muted-foreground">
              {selectedItems.length} appointment{selectedItems.length === 1 ? "" : "s"}
            </span>
          </div>
          {/* ⚠️ A PLAIN overflow div, NOT Radix ScrollArea.
              ScrollArea's Viewport is h-full, so a max-h on its Root gives it no
              definite height to resolve against — the viewport grew with the
              content and the Root's overflow-hidden simply clipped it. With five
              appointments on one day the last two were unreachable. */}
          <div className="max-h-72 overflow-y-auto">
            <div className="space-y-2 pr-3">
              {selectedItems.map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${KIND_DOT[it.ownerKind]}`} />
                      <span className="truncate text-sm font-medium">{it.leadName || it.title}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {new Date(it.startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                      {/* ⚠️ For API bookings, bookedByName is NOT the booker.
                          appointments.user_id is the triage OWNER the booking was
                          parked on (Tobe, Mark — or a test account literally named
                          "agent"), so "Booked by Tobe · Unassigned" read as if Tobe
                          booked it and then failed to assign it. He did neither: the
                          Live Transfer AI booked it and it is waiting for him. Say so. */}
                      {it.bookedViaApi ? (
                        <span>
                          Booked by <span className="font-medium">Live Transfer AI</span>
                          {it.bookedByName && !it.assignedToName ? ` · for ${it.bookedByName}` : ""}
                        </span>
                      ) : (
                        <span>Booked by {it.bookedByName || "—"}</span>
                      )}
                      <span className="text-muted-foreground/50">·</span>
                      <span>
                        {/* ⚠️ READ ownerKind, NOT just assignedToName.
                            An agent booking their own lead has no assignee and
                            never needs one — it is already theirs. Deciding on
                            an empty assignedToName alone stamped those
                            "Unassigned" and offered a handoff, which contradicted
                            the green dot beside it. Only a genuinely ownerless
                            appointment says Unassigned. */}
                        {it.assignedToName ? (
                          <span className="font-medium text-emerald-600 dark:text-emerald-400">
                            Assigned to {it.assignedToName}
                          </span>
                        ) : it.ownerKind === "agent" ? (
                          // Nothing to add: "Booked by X" already names the holder,
                          // because bookedByName joins on appointments.user_id — the
                          // calendar this sits on. Repeating it as "Owned by X" said
                          // the same name twice in one line.
                          <span className="font-medium text-emerald-600 dark:text-emerald-400">
                            Theirs
                          </span>
                        ) : (
                          <span className="font-medium text-red-600 dark:text-red-400">Unassigned</span>
                        )}
                      </span>
                      {includeSubAgencies && it.agencyName && (
                        <>
                          <span className="text-muted-foreground/50">·</span>
                          <span>{it.agencyName}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="sm" variant="outline" className="shrink-0 gap-1.5">
                        <UserPlus className="h-3.5 w-3.5" />
                        {it.assignedToUserId || it.ownerKind === "agent" ? "Reassign" : "Assign"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-72 p-0">
                      <div className="border-b px-3 py-2 text-xs font-semibold text-muted-foreground">
                        <Users className="mr-1 inline h-3.5 w-3.5" /> Tag an agent
                      </div>
                      {/* Search: a downline can run to hundreds of agents, and
                          scrolling a flat list to find one is not a picker. */}
                      <div className="border-b p-2">
                        <Input
                          autoFocus
                          value={agentQuery[it.id] ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setAgentQuery((q) => ({ ...q, [it.id]: e.target.value }))
                          }
                          placeholder="Search by name, email or agency…"
                          className="h-8 text-sm"
                        />
                      </div>
                      {/* Plain overflow div — same Radix ScrollArea caveat as the
                          day panel above. */}
                      <div className="max-h-60 overflow-y-auto">
                        <div className="p-1">
                          {(() => {
                            const q = (agentQuery[it.id] ?? "").trim().toLowerCase();
                            const shown = (members ?? []).filter((m) =>
                              !q ||
                              [m.name, m.email, m.agencyName]
                                .filter(Boolean)
                                .some((v) => String(v).toLowerCase().includes(q)),
                            );
                            if (!members?.length)
                              return <div className="px-2 py-3 text-xs text-muted-foreground">No agents found</div>;
                            if (!shown.length)
                              return (
                                <div className="px-2 py-3 text-xs text-muted-foreground">
                                  No agent matches “{agentQuery[it.id]}”
                                </div>
                              );
                            return shown.map((m) => (
                              <button
                                key={m.id}
                                type="button"
                                disabled={assign.isPending}
                                onClick={() =>
                                  assign.mutate({ appointmentId: it.id, assignedToUserId: m.id })
                                }
                                className="flex w-full items-start justify-between gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted"
                              >
                                <span className="min-w-0">
                                  <span className="block truncate text-sm">
                                    {m.name || m.email}
                                    {m.role ? (
                                      <span className="ml-1 text-[10px] uppercase text-muted-foreground">
                                        {m.role}
                                      </span>
                                    ) : null}
                                  </span>
                                  {/* Which agency — two people share a first name
                                      often enough that the name alone is not an
                                      identifier across a downline. */}
                                  {m.agencyName ? (
                                    <span className="block truncate text-[11px] text-muted-foreground">
                                      {m.agencyName}
                                      {m.extraAgencies
                                        ? ` +${m.extraAgencies} more`
                                        : ""}
                                    </span>
                                  ) : null}
                                </span>
                                {it.assignedToUserId === m.id && (
                                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                                )}
                              </button>
                            ));
                          })()}
                        </div>
                      </div>
                      {it.assignedToUserId && (
                        <div className="border-t p-1">
                          <button
                            type="button"
                            disabled={assign.isPending}
                            onClick={() => assign.mutate({ appointmentId: it.id, assignedToUserId: null })}
                            className="w-full rounded-md px-2 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                          >
                            Clear assignment
                          </button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="rounded-xl border border-dashed py-12 text-center text-sm text-muted-foreground">
          No appointments for {monthLabel}.
        </div>
      )}
    </div>
  );
}

export default AgencyCalendar;
